"use client";
import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, Variants, useInView } from "framer-motion";
import TechCategoryBanner from "@/components/animated-svg/tech-category-banner";
import ExpertiseHeaderAnimated from "@/components/animated-svg/expertise-header";
import { useTranslations } from "next-intl";

const Technologies = () => {
  const t = useTranslations("technologies");
  const [activeTab, setActiveTab] = useState("Languages");
  const [cardMinHeight, setCardMinHeight] = useState(0);
  const prevIndexRef = useRef(0);
  const measureRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "0px 0px -80px 0px" });

  // Infinite pulse animation for background glow (no view-based toggling)
  const bgShapeVariants: Variants = {
    visible: {
      opacity: 1,
      rotate: 90,
      scale: [0.9, 1.04, 0.9] as number[],
      transition: {
        duration: 6,
        ease: "easeInOut",
        repeat: Infinity,
        repeatType: "loop",
      },
    },
  };

  const tabs = [
    {
      name: "Languages",
      key: "languages",
      items: ["JavaScript", "TypeScript", "Swift", "PHP"],
    },
    {
      name: "Frontend",
      key: "frontend",
      items: ["React", "Next.js", "Tailwind CSS", "Framer Motion"],
    },
    {
      name: "Backend",
      key: "backend",
      items: ["Node.js", "Laravel", "REST API", "GraphQL"],
    },
    {
      name: "Mobile",
      key: "mobile",
      items: ["React Native", "Swift", "Capacitor"],
    },
    {
      name: "Cloud & Hosting",
      key: "cloud",
      items: ["AWS", "Google Cloud", "DigitalOcean", "Vercel", "Firebase", "Northflank", "VPS", "Custom Web Servers"],
    },
    {
      name: "Security",
      key: "security",
      items: ["OAuth2", "JWT", "Auth0", "HTTPS / SSL", "Rate Limiting", "Code Audits"],
    },
    {
      name: "Tooling",
      key: "tooling",
      items: ["Git / GitHub", "Jira", "ClickUp", "Figma", "Jest", "Cypress", "Postman", "Scalar"],
    },
  ];

  const currentTab = tabs.find((tab) => tab.name === activeTab);

  // Measure all tabs to find the tallest card height
  useEffect(() => {
    const container = measureRef.current;
    if (!container) return;
    const children = container.children;
    let max = 0;
    for (let i = 0; i < children.length; i++) {
      max = Math.max(max, (children[i] as HTMLElement).offsetHeight);
    }
    if (max > 0) setCardMinHeight(max);

    const onResize = () => {
      let m = 0;
      for (let i = 0; i < children.length; i++) {
        m = Math.max(m, (children[i] as HTMLElement).offsetHeight);
      }
      if (m > 0) setCardMinHeight(m);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <section ref={sectionRef} className="section pt-[40px] md:pt-[56px] lg:pt-[50px] pb-[40px] md:pb-[50px] lg:pb-[50px] relative">
      <div className="absolute inset-0 -z-[1] hidden lg:block">
        <ExpertiseHeaderAnimated />
      </div>

      <div className="section-header mb-[30px] lg:mb-[30px] text-center">
        <div className="container">
          <motion.div
            className="mb-[60px]"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h2 className="text-[28px] md:text-[40px] lg:text-[60px] font-semibold leading-[1.2]">
              {t("title")} <br />
              {t("titleLine2")}
            </h2>
          </motion.div>
        </div>

        <motion.div
          className="container !max-w-[860px]"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
        >
          <ul role="tablist" className="relative flex gap-[1px] items-center bg-[linear-gradient(180deg,_rgba(255,_255,_255,_0.1)_0%,_rgba(255,_255,_255,_0)_100%)] rounded-[8px] p-0 overflow-x-auto scrollbar-hide">
            {tabs.map((tab, index) => (
              <motion.li
                key={tab.name}
                role="presentation"
                className="relative"
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                transition={{
                  duration: 0.5,
                  delay: 0.3 + index * 0.1,
                  ease: "easeOut",
                }}
              >
                {activeTab === tab.name && (
                  <motion.span
                    layoutId="techTabBg"
                    className="absolute inset-0 bg-[#4a3290] rounded-[6px]"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
                <motion.button
                  role="tab"
                  aria-selected={activeTab === tab.name}
                  tabIndex={activeTab === tab.name ? 0 : -1}
                  className={`relative z-10 technology-navbar__item text-[14px] lg:text-[16px] leading-[1.5] inline-flex cursor-pointer font-light rounded-[6px] px-[18px] lg:px-[20px] lg:py-[12px] py-[10px] whitespace-nowrap transition-colors duration-300 ${
                    activeTab === tab.name
                      ? "text-white"
                      : "text-white/65 hover:text-white"
                  }`}
                  onClick={(e) => {
                    const newIndex = tabs.findIndex((t) => t.name === tab.name);
                    prevIndexRef.current = newIndex;
                    setActiveTab(tab.name);
                    (e.currentTarget as HTMLElement).scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                  }}
                  onKeyDown={(e) => {
                    const currentIndex = tabs.findIndex((t) => t.name === tab.name);
                    let nextIndex = currentIndex;
                    if (e.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
                    else if (e.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
                    else if (e.key === 'Home') nextIndex = 0;
                    else if (e.key === 'End') nextIndex = tabs.length - 1;
                    else return;
                    e.preventDefault();
                    prevIndexRef.current = nextIndex;
                    setActiveTab(tabs[nextIndex].name);
                    const buttons = e.currentTarget.closest('ul')?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
                    buttons?.[nextIndex]?.focus();
                    buttons?.[nextIndex]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                  }}
                >
                  {t(`tabs.${tab.key}.title`)}
                </motion.button>
              </motion.li>
            ))}
          </ul>
        </motion.div>
      </div>

      {/* Hidden measurement container — renders all tabs off-screen to find tallest */}
      <div ref={measureRef} className="absolute opacity-0 pointer-events-none -z-50 w-full" style={{ maxWidth: 860 }} aria-hidden="true">
        {tabs.map((tab) => (
          <div key={tab.key} className="grid items-stretch grid-cols-1 md:grid-cols-[1fr_360px]">
            <div className="p-[24px] md:p-[40px] lg:p-[56px] flex flex-col justify-center">
              <h3 className="text-[24px] md:text-[32px] lg:text-[42px] font-semibold leading-[1.2] mb-[15px] lg:mb-[24px]">
                {t(`tabs.${tab.key}.title`)}
              </h3>
              <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 mb-[16px]">
                {t(`tabs.${tab.key}.description`)}
              </p>
              <p className="text-[13px] font-light leading-[1.8] text-white/65 tracking-wide">
                {tab.items.join(" · ")}
              </p>
            </div>
            <div className="min-h-[220px] md:min-h-[320px]" />
          </div>
        ))}
      </div>

      <motion.div
        className="container !max-w-[860px] mt-[50px] lg:mt-[60px]"
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
        transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
      >
        <div className="relative pt-[12px]">
          {/* Stacked background cards */}
          <motion.div
            key={`stack-3-${activeTab}`}
            className="absolute inset-x-0 top-0 bottom-0 rounded-[24px] bg-[linear-gradient(0deg,_#121124_0%,_#353353_100%)] p-[1px] z-0"
            initial={{ rotate: 0 }}
            animate={{ rotate: 2.5, y: -6 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="w-full h-full rounded-[24px] bg-gradient-to-b from-[#0a0619] to-[#080516]" />
          </motion.div>
          <motion.div
            key={`stack-2-${activeTab}`}
            className="absolute inset-x-0 top-0 bottom-0 rounded-[24px] bg-[linear-gradient(0deg,_#121124_0%,_#353353_100%)] p-[1px] z-[1]"
            initial={{ rotate: 0 }}
            animate={{ rotate: -1.5, y: -3 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="w-full h-full rounded-[24px] bg-gradient-to-b from-[#0e0b22] to-[#0a0619]" />
          </motion.div>

          {/* Front card */}
          <div aria-live="polite" aria-atomic="true">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10, scale: 0.98 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-[24px] border border-white/[0.07] bg-gradient-to-br from-[#12102a] to-[#0e0821] overflow-hidden relative z-[2] flex flex-col"
              style={cardMinHeight ? { minHeight: cardMinHeight } : undefined}
            >
              <motion.div
                className="absolute top-0 right-0 w-[200px] h-[200px] bg-theme/10 rounded-full blur-[80px] pointer-events-none z-0"
                variants={bgShapeVariants}
                initial="visible"
                animate="visible"
              />
              <div className="grid items-stretch grid-cols-1 md:grid-cols-[1fr_360px] relative z-10 flex-1">
                {/* Left: text */}
                <div className="p-[24px] md:p-[40px] lg:p-[56px] flex flex-col justify-center">
                  <h3 className="text-[24px] md:text-[32px] lg:text-[42px] font-semibold leading-[1.2] mb-[15px] lg:mb-[24px]">
                    {currentTab ? t(`tabs.${currentTab.key}.title`) : ""}
                  </h3>
                  <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 mb-[16px]">
                    {currentTab ? t(`tabs.${currentTab.key}.description`) : ""}
                  </p>
                  <p className="text-[13px] font-light leading-[1.8] text-white/65 tracking-wide">
                    {currentTab?.items.join(" · ")}
                  </p>
                </div>

                {/* Right: banner */}
                <div className="relative border-t md:border-t-0 md:border-l border-white/[0.07] min-h-[220px]">
                  {currentTab && (
                    <TechCategoryBanner
                      category={currentTab.name}
                      items={currentTab.items}
                    />
                  )}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
          </div>
        </div>
      </motion.div>

      {/* BACKGROUND SHAPE */}
      <motion.div
        className="absolute -bottom-[10vw] -end-[10vw] w-[480px] h-[480px] bg-[#7836D0] blur-[350px] rotate-90"
        variants={bgShapeVariants}
        initial="visible"
        animate="visible"
      />
    </section>
  );
};

export default Technologies;
